import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { RELATIONSHIP_OPTIONS } from '../../constants/relationshipOptions';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: string;
  notes?: string;
}

interface CSVContactImportProps {
  onImport: (contacts: Contact[]) => void;
  onError: (error: string) => void;
}

interface CSVRow {
  'First Name': string;
  'Last Name': string;
  'Email': string;
  'Phone': string;
  'Relationship': string;
  'Notes'?: string;
}

const CSVContactImport: React.FC<CSVContactImportProps> = ({ onImport, onError }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateContact = (contact: CSVRow): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Required fields
    if (!contact['First Name']?.trim()) errors.push('First Name is required');
    if (!contact['Last Name']?.trim()) errors.push('Last Name is required');
    if (!contact['Relationship']?.trim()) errors.push('Relationship is required');

    // At least one contact method required
    if (!contact['Email']?.trim() && !contact['Phone']?.trim()) {
      errors.push('Either Email or Phone is required');
    }

    // Email format validation
    if (contact['Email']?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact['Email'].trim())) {
      errors.push('Invalid email format');
    }

    // Phone format validation
    if (contact['Phone']?.trim() && !/^\+?[\d\s-()]{10,}$/.test(contact['Phone'].trim())) {
      errors.push('Invalid phone format');
    }

    // Relationship validation
    const validRelationships = RELATIONSHIP_OPTIONS.map(r => r.toLowerCase());
    if (!validRelationships.includes(contact['Relationship']?.toLowerCase().trim())) {
      errors.push('Invalid relationship type');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const processCSV = (file: File) => {
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const contacts: Contact[] = [];
        const errors: string[] = [];

        results.data.forEach((row, index) => {
          const validation = validateContact(row);
          
          if (validation.isValid) {
            contacts.push({
              id: `csv-${Date.now()}-${index}`,
              firstName: row['First Name'].trim(),
              lastName: row['Last Name'].trim(),
              email: row['Email']?.trim() || '',
              phone: row['Phone']?.trim() || '',
              relationship: row['Relationship'].toLowerCase().trim(),
              notes: row['Notes']?.trim()
            });
          } else {
            errors.push(`Row ${index + 2}: ${validation.errors.join(', ')}`);
          }
        });

        if (errors.length > 0) {
          onError(`CSV Import Errors:\n${errors.join('\n')}`);
        }

        if (contacts.length > 0) {
          onImport(contacts);
        }
      },
      error: (error) => {
        onError(`Failed to parse CSV: ${error.message}`);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/csv') {
      processCSV(file);
    } else {
      onError('Please upload a CSV file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processCSV(file);
    }
  };

  const downloadTemplate = () => {
    const template = 'First Name,Last Name,Email,Phone,Relationship,Notes\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Template Download */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-stone-800">Import Contacts from CSV</h3>
        <button
          onClick={downloadTemplate}
          className="text-sm text-cyan-700 hover:text-cyan-800 underline"
        >
          Download Template
        </button>
      </div>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-cyan-500 bg-cyan-50'
            : 'border-stone-300 hover:border-cyan-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="space-y-2">
          <svg
            className="w-12 h-12 mx-auto text-stone-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <div className="text-stone-600">
            <p className="font-medium">
              Drop your CSV file here, or click to select
            </p>
            <p className="text-sm">
              Make sure your CSV matches the template format
            </p>
          </div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default CSVContactImport;
